import { useEffect, useState } from "react"
import { Container, Row, Col, Button, Modal, Table, Form, InputGroup, Dropdown } from 'react-bootstrap'
import OrganisationSelector from "./common/organisationSelector";
import Loading from "./common/loading"
import useApi from './../utilities/useApi';
import DatePicker from 'react-datepicker';
import arrayUtilities from "../utilities/arrayUtilities";
import dateUtilities from "../utilities/dateUtilities";
import { DataPagination } from "./common/pagination";
import enums from "../utilities/enums";
import { ColumnSortTrigger } from "./common/columnSortTrigger";
import { useNavigate } from "react-router-dom";
import { saveAs } from 'file-saver';
import FilterBar from './common/filterBar'

export default function Licenses(props) {

    const FILTERS_STORAGE_KEY = 'licenses-filters';

    const loadStoredFilters = () => {
        try {
            const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
            if (!raw) return {};
            const parsed = JSON.parse(raw);
            return {
                ...parsed,
                fromDate: parsed.fromDate ? new Date(parsed.fromDate) : null,
                toDate: parsed.toDate ? new Date(parsed.toDate) : null,
            };
        } catch {
            return {};
        }
    };

    const paginationSeedNumber = 100;
    const { api } = useApi();
    const navigate = useNavigate()
    const storedFilters = loadStoredFilters();
    const [isLoading, setIsLoading] = useState(false)
    const [licenses, setLicenses] = useState(null);
    const [fromDate, setFromDate] = useState(storedFilters.fromDate ?? null);
    const [toDate, setToDate] = useState(storedFilters.toDate ?? null);
    const [includeDeleted, setIncludeDeleted] = useState(storedFilters.includeDeleted ?? false);
    const [page, setPage] = useState(storedFilters.page ?? 0);
    const [organizationalUnitId, setOrganizationalUnitId] = useState(storedFilters.organizationalUnitId ?? null);
    const [numberOfResultsPages, setNumberOfResultsPages] = useState(0);
    const [multiCheckedIds, setMultiCheckedIds] = useState([]);
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
    const [actionModal, setActionModal] = useState(null);
    const [errorModal, setErrorModal] = useState(null)
    const [currentSortBy, setCurrentSortBy] = useState(storedFilters.currentSortBy ?? null);
    const [sid, setSid] = useState(storedFilters.sid ?? "");
    const [billTo, setBillTo] = useState("");
    const [renewalDate, setRenewalDate] = useState(dateUtilities.addYears(new Date(), 1));
    const [adminEmail, setAdminEmail] = useState("");
    const [showBulkEditRenewalDateModal, setShowBulkEditRenewalDateModal] = useState(false);
    const [showBulkEditAdminEmailModal, setShowBulkEditAdminEmailModal] = useState(false);
    const [showBulkEditBillToModal, setShowBulkEditBillToModal] = useState(false);

    var _getDataAbortController = null;
    const fetchLicenses = (sortCategory, sortOrder, orgId, startDate, endDate, showDeleted, page, sid) => {
        setIsLoading(true)
        if (_getDataAbortController) {
            _getDataAbortController.abort()
        };

        _getDataAbortController = new AbortController();
        api.getLicenses(
            sortCategory, sortOrder, 0, orgId, startDate, endDate, showDeleted, page, false, sid, _getDataAbortController.signal,
            data => {
                setLicenses(data)
                if (!arrayUtilities.isNullOrEmpty(data.items)) {
                    setNumberOfResultsPages(Math.ceil(data.totalCount / paginationSeedNumber))
                }
                setIsLoading(false)
            }, 
            error => {
                setErrorModal(error.errorMessage);
                setIsLoading(false)
            }
        )
    }
    useEffect(() => fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid), [])

    useEffect(() => {
        sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
            fromDate,
            toDate,
            includeDeleted,
            page,
            organizationalUnitId,
            currentSortBy,
            sid,
        }));
    }, [fromDate, toDate, includeDeleted, page, organizationalUnitId, currentSortBy, sid])

    const handleSelectionUpdate = (e, licenseItem) => {
        if (e.target.checked) {
            setMultiCheckedIds(prevState => [...prevState, licenseItem.sid])
            if (licenses.items.length - multiCheckedIds.length === 1) {
                setIsSelectAllChecked(true)
            }
        } else if (!e.target.checked) {
            const index = multiCheckedIds.findIndex(item => item === licenseItem.sid)
            setMultiCheckedIds(prevState => prevState.toSpliced(index, 1))

            if (isSelectAllChecked) {
                setIsSelectAllChecked(false)
            }
        }
    }

    const handleSelectAll = (e) => {
        setIsSelectAllChecked(!isSelectAllChecked)
        if (e.target.checked) {
            setMultiCheckedIds(licenses.items.map(item => item.sid))
        } else if (!e.target.checked) {
            setMultiCheckedIds([])
        }
    }

    const unDeleteLicense = (targetSid) => {
        api.unDeleteLicense(
            targetSid,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid);
                setActionModal(null)
            },
            error => {
                setActionModal(null)
                setErrorModal(error.errorMessage);
            }
        )
    }

    const deleteLicense = (targetSid) => {
        api.deleteLicense(
            targetSid,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid);
                setActionModal(null)
            },
            error => {
                setActionModal(null)
                setErrorModal(error.errorMessage);
            }
        )
    }

    const clearBulkSelection = () => {
        setMultiCheckedIds([])
        setIsSelectAllChecked(false)
    }

    const deleteMultiLicenses = (sids) => {
        api.deleteMultiLicenses(
            sids,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid);
                clearBulkSelection()
                setActionModal(null)
            },
            error => {
                setActionModal(null)
                setErrorModal(error.errorMessage)
            }
        )
    }

    const unDeleteMultiLicenses = (sids) => {
        api.unDeleteMultiLicenses(
            sids,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid)
                clearBulkSelection()
                setActionModal(null)
            },
            error => {
                setActionModal(null)
                setErrorModal(error.errorMessage)
            }
        )
    }

    const enableMultiLicenses = (sids) => {
        api.enableMultiLicenses(
            sids,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid)
                clearBulkSelection()
                setActionModal(null)
            },
            error => {
                setActionModal(null)
                setErrorModal(error.errorMessage)
            }
        )
    }

    const disableMultiLicenses = (sids) => {
        api.disableMultiLicenses(
            sids,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid)
                clearBulkSelection()
                setActionModal(null)
            },
            error => {
                setActionModal(null)
                setErrorModal(error.errorMessage)
            }
        )
    }

    const bulkUpdateRenewalDate = (sids) => {
        api.editMultiLicenses(
            sids, null, renewalDate, null,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid)
                clearBulkSelection()
                setShowBulkEditRenewalDateModal(false)
            },
            error => setErrorModal(error.errorMessage)
        )
    }

    const bulkUpdateAdminEmail = (sids) => {
        api.editMultiLicenses(
            sids, null, null, adminEmail,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid)
                clearBulkSelection()
                setShowBulkEditAdminEmailModal(false)
            },
            error => setErrorModal(error.errorMessage)
        )
    }

    const bulkUpdateBillTo = (sids) => {
        api.editMultiLicenses(
            sids, billTo, null, null,
            () => {
                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid)
                clearBulkSelection()
                setShowBulkEditBillToModal(false)
            },
            error => setErrorModal(error.errorMessage)
        )
    }

    const handleSortTriggerClick = (sortBy, sortDirection) => {

        fetchLicenses(sortBy, enums.SortDirection[sortDirection], organizationalUnitId, fromDate, toDate, includeDeleted, page, sid)
        if (currentSortBy !== sortBy) {
            setCurrentSortBy(sortBy)
        }
    }

    const setLicenseEnabled = (targetSid, enabled) => {
        if (enabled) {
            api.enableLicense(
                targetSid,
                () => fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid),
                error => setErrorModal(error.errorMessage)
            )
        } else {
            api.disableLicense(
                targetSid,
                () => fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, page, sid),
                error => setErrorModal(error.errorMessage)
            )
        }
    }

    const clearFilters = () => {
        setFromDate(null);
        setToDate(null);
        setPage(0);
        setOrganizationalUnitId(null)
        setIncludeDeleted(false)
        setSid("")
        fetchLicenses(null, null, null, null, null, null, 0, "")
    }

    const handleExport = () => {
        api.getLicenses(
            currentSortBy, null, 3, organizationalUnitId, fromDate, toDate, includeDeleted, page, false, sid, null,
            (blob, filename) => {saveAs(blob, filename)},
            error => setErrorModal(error.errorMessage)
        )
    }

    return (
        <Container className='pt-5 pb-lg-4 mt-5 mb-sm-2 px-4' fluid>
            <Row>
                <Col className='mb-5'>
                    <div className='d-flex flex-column justify-content-between py-4 bg-light sticky-top' style={{ top: '75px' }}>
                        <h1 className="h2 m-0">Licenses</h1>
                        <div className='d-flex gap-2 align-items-center w-100 justify-content-between'>
                            <div className="d-flex">
                                <Form.Group className='d-flex align-items-center me-3'>
                                    <div className="d-flex align-items-center">
                                        <Form.Label className='text-body fs-sm mb-0 pe-1 text-nowrap'>Include deleted</Form.Label>
                                        <InputGroup size="sm">
                                            <Form.Check type='switch' id="include-deleted" className="me-2 pt-2" checked={ includeDeleted } onChange={ e => {
                                                setIncludeDeleted(e.target.checked);
                                                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, e.target.checked, page, sid)
                                            } } />
                                        </InputGroup>
                                    </div>
                                    <FilterBar value={sid} onChange={ e => {
                                            setSid(e);
                                            setPage(0);
                                            fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, 0, e)
                                        }
                                    } className="me-4" />
                                    <Form.Control
                                        as={ DatePicker }
                                        selected={ fromDate }
                                        value={ fromDate }
                                        onChange={ (date) => {
                                            setFromDate(date);
                                            setPage(0)
                                            fetchLicenses(currentSortBy, null, organizationalUnitId, date, toDate, includeDeleted, 0, sid)
                                        } }
                                        placeholderText="Start Date"
                                        dateFormat="dd/MM/yyyy"
                                        className='rounded-start form-control-sm cursor-pointer'
                                    />
                                    <Form.Control
                                        as={ DatePicker }
                                        selected={ toDate }
                                        value={ toDate }
                                        onChange={ (date) => {
                                            setToDate(date);
                                            setPage(0);
                                            fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, date, includeDeleted, 0, sid)
                                        } }
                                        placeholderText="End Date"
                                        dateFormat="dd/MM/yyyy"
                                        className='rounded-start-0 form-control-sm cursor-pointer'
                                    />
                                </Form.Group>
                                
                                <OrganisationSelector value={ organizationalUnitId } organisations={ props.organisationalUnits } onChange={ id => {
                                    setOrganizationalUnitId(id);
                                    fetchLicenses(currentSortBy, null, id, fromDate, toDate, includeDeleted, 0, sid)
                                }} allowNone />
                                <Button size='sm' variant='secondary' onClick={ () => clearFilters() }>
                                    <span className='d-flex align-items-center'><i className='fi-refresh me-2'></i>
                                        Clear Filters
                                    </span>
                                </Button>
                            </div>
                            <div className='d-flex gap-2 align-items-center'>
                                <Button size='sm' variant='secondary' onClick={ () => handleExport() }>
                                    <i className='fi-download me-2'></i>
                                    Export
                                </Button>
                                <Button size='sm' onClick={ () => navigate(`/${props.organisationId}/admin-tools/licenses/add`) }>
                                    <i className='fi-plus me-2'></i>
                                    Add License
                                </Button>
                                <Dropdown>
                                    <Dropdown.Toggle disabled={arrayUtilities.isNullOrEmpty(multiCheckedIds)} className='btn-sm btn-secondary'>Bulk Actions</Dropdown.Toggle>
                                    <Dropdown.Menu align='end'>
                                        <Dropdown.Item onClick={ () => {
                                            const selectedRenewalDates = (licenses?.items ?? [])
                                                .filter(item => multiCheckedIds.includes(item.sid) && item.renewalDate)
                                                .map(item => new Date(item.renewalDate));
                                            const baseDate = selectedRenewalDates.length > 0
                                                ? new Date(Math.min(...selectedRenewalDates.map(d => d.getTime())))
                                                : new Date();
                                            setRenewalDate(dateUtilities.addYears(baseDate, 1));
                                            setShowBulkEditRenewalDateModal(true);
                                        }}>
                                            <i className='fi-calendar me-2'></i> Update Renewal Date
                                        </Dropdown.Item>

                                        <Dropdown.Item onClick={ () => setShowBulkEditAdminEmailModal(true)}>
                                            <i className='fi-security me-2'></i> Update Admin Email
                                        </Dropdown.Item>

                                        <Dropdown.Item onClick={() => setShowBulkEditBillToModal(true)}>
                                            <i className='fi-accounting me-2'></i> Update Bill To Email
                                        </Dropdown.Item>
                                        
                                        <Dropdown.Item onClick={ () => {
                                            setActionModal({
                                                title: "Delete Licenses",
                                                content: <p>Are you sure you want to delete {multiCheckedIds.length} licenses?</p>,
                                                onConfirm: () => deleteMultiLicenses(multiCheckedIds)
                                            })
                                        } }><i className='fi-trash me-2'></i> Delete</Dropdown.Item>
                                        <Dropdown.Item onClick={ () => {
                                            setActionModal({
                                                title: "Un-delete Licenses",
                                                content: <p>Are you sure you want to un-delete {multiCheckedIds.length} licenses?</p>,
                                                onConfirm: () => unDeleteMultiLicenses(multiCheckedIds)
                                            })
                                        } }><i className='fi-rotate-left me-2'></i> Un-delete</Dropdown.Item>
                                        <Dropdown.Item onClick={ () => {
                                            setActionModal({
                                                title: "Enable Licenses",
                                                content: <p>Are you sure you want to enable {multiCheckedIds.length} licenses?</p>,
                                                onConfirm: () => enableMultiLicenses(multiCheckedIds)
                                            })
                                        } }><i className='fi-checkbox-checked-alt me-2'></i> Enable</Dropdown.Item>
                                        <Dropdown.Item onClick={ () => {
                                            setActionModal({
                                                title: "Disable Licenses",
                                                content: <p>Are you sure you want to disable {multiCheckedIds.length} licenses?</p>,
                                                onConfirm: () => disableMultiLicenses(multiCheckedIds)
                                            })
                                        } }><i className='fi-minus-circle me-2'></i> Disable</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                    {
                        isLoading
                            ? <Loading />
                            : <>
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>
                                                <input name="select-all" class="form-check-input cursor-pointer" value={ isSelectAllChecked } checked={ isSelectAllChecked } type="checkbox" onChange={ e => handleSelectAll(e) } />
                                            </th>
                                            <th>
                                                <ColumnSortTrigger 
                                                    onClick={ direction => handleSortTriggerClick("sid", direction) } 
                                                    currentSortBy={currentSortBy}
                                                    sortBy={"sid"} 
                                                    label="SID"
                                                />
                                            </th>
                                            <th>Organisation</th>
                                            <th>SKU</th>
                                            <th>Assigned To</th>
                                            <th>Role</th>
                                            <th>
                                                <ColumnSortTrigger 
                                                    onClick={ direction => handleSortTriggerClick("renewalDate", direction) } 
                                                    currentSortBy={currentSortBy}
                                                    sortBy={"renewalDate"} 
                                                    label="Renewal"
                                                />
                                            </th>
                                            <th>
                                                <ColumnSortTrigger 
                                                    onClick={ direction => handleSortTriggerClick("adminEmail", direction) } 
                                                    currentSortBy={currentSortBy}
                                                    sortBy={"adminEmail"} 
                                                    label="Admin Email"
                                                />
                                            </th>
                                            <th>Bill To</th>
                                            <th className='text-center'>Enabled</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            !arrayUtilities.isNullOrEmpty(licenses?.items) && licenses?.items.map(
                                                license => (
                                                    <tr key={ license.sid } className='align-middle'>
                                                        <td>
                                                            <input
                                                                id={ license.sid }
                                                                onChange={ e => handleSelectionUpdate(e, license) }
                                                                className="form-check-input cursor-pointer"
                                                                type="checkbox"
                                                                checked={ multiCheckedIds?.includes(license.sid) }
                                                            />
                                                        </td>
                                                        <td><span className={`${license.isDeleted && "text-muted"}`}>{ license.sid }</span></td>
                                                        <td><span className={`${license.isDeleted && "text-muted"}`}>{ license.organisationalUnitName }</span></td>
                                                        <td><span className={`${license.isDeleted && "text-muted"}`}>{ license.skuId }</span></td>
                                                        <td><span className={`${license.isDeleted && "text-muted"}`}>{license.user}</span></td>
                                                        <td><span className={`${license.isDeleted && "text-muted"}`}>{enums.UserRole[license.role]}</span></td>
                                                        <td><span className={`${license.isDeleted && "text-muted"}`}>{ dateUtilities.formatToDateString(license.renewalDate) }</span></td>
                                                        <td><span className={`${license.isDeleted && "text-muted"}`}>{ license.adminEmail }</span></td>
                                                        <td><span className={`${license.isDeleted && "text-muted"}`}>{ license.billTo }</span></td>
                                                        <td className='text-center'>
                                                            <Form.Check type='switch' id={ `license-${ license.sid }-enabled` } className="me-2" value={ license.sid } checked={license.isEnabled} defaultChecked={ license.isEnabled } disabled={ license.isDeleted } onChange={ (e) => setLicenseEnabled(license.sid, e.target.checked) } />
                                                        </td>
                                                        <td className="text-end">
                                                            <Dropdown>
                                                                <Dropdown.Toggle className='bg-none btn-secondary organisation-options-dropdown-toggle'><i className='fi-dots-vertical'></i></Dropdown.Toggle>
                                                                <Dropdown.Menu align='end' className='my-1'>
                                                                    {
                                                                        license.isDeleted &&
                                                                        <Dropdown.Item onClick={ () => {
                                                                            setActionModal({
                                                                                title: "Un-delete License",
                                                                                content: <>Are you sure you want to un-delete the license { license.sid }?</>,
                                                                                onConfirm: () => unDeleteLicense(license.sid)
                                                                            })
                                                                        } }>
                                                                            Un-delete
                                                                        </Dropdown.Item>
                                                                    }
                                                                    {
                                                                        !license.isDeleted &&
                                                                        <>
                                                                            <Dropdown.Item onClick={ () => navigate(`/${props.organisationId}/admin-tools/licenses/${license.sid}`) }>Edit</Dropdown.Item>
                                                                            <Dropdown.Item onClick={ () => {
                                                                                setActionModal({
                                                                                    title: "Delete License",
                                                                                    content: <p>Are you sure you want to delete the license { license.sid }?</p>,
                                                                                    onConfirm: () => deleteLicense(license.sid)
                                                                                })
                                                                            } }>
                                                                                Delete
                                                                            </Dropdown.Item>
                                                                        </>
                                                                    }
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        }
                                    </tbody>
                                </Table>
                                {
                                    licenses && !arrayUtilities.isNullOrEmpty(licenses.items)
                                    && <div className='d-flex justify-content-center'>
                                        <DataPagination
                                            totalResults={ licenses.totalCount }
                                            page={ page }
                                            onPageNumberClick={ pageNumber => {
                                                setPage(pageNumber)
                                                fetchLicenses(currentSortBy, null, organizationalUnitId, fromDate, toDate, includeDeleted, pageNumber, sid)
                                            } }
                                            paginationSeedNumber={ paginationSeedNumber }
                                            numberOfResultsPages={ numberOfResultsPages }
                                        />
                                    </div>
                                }
                            </>
                    }
                </Col>
            </Row>
            {
                actionModal &&
                <Modal show onHide={ () => setActionModal(null)} centered>
                    <Modal.Header>
                        <Modal.Title>{ actionModal.title }</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        { actionModal.content }
                    </Modal.Body>
                    <Modal.Footer>
                        <Button size="sm" variant='secondary' onClick={ () => setActionModal(null) }>Cancel</Button>
                        <Button size="sm" variant="primary" onClick={ actionModal.onConfirm }>Confirm</Button>
                    </Modal.Footer>
                </Modal>
            }

            {
                showBulkEditRenewalDateModal && 
                <Modal show onHide={ () => {
                    setShowBulkEditRenewalDateModal(false)
                    setRenewalDate(dateUtilities.addYears(new Date(), 1))
                }} centered>
                    <Modal.Header>
                        <Modal.Title>Update Renewal Date</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className='mb-3 row col-12'>
                            <Form.Label className='form-label fs-sm col-form-label col-md-3'>
                                Renewal Date
                            </Form.Label>
                            <div className="col-md-9 d-flex align-items-center">
                                <Form.Control
                                    as={ DatePicker }
                                    selected={ renewalDate }
                                    value={renewalDate}
                                    onChange={ date => setRenewalDate(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className='rounded-start form-control-sm cursor-pointer'
                                />
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button size="sm" variant='secondary' onClick={ () => {
                            setShowBulkEditRenewalDateModal(false)
                            setRenewalDate(dateUtilities.addYears(new Date(), 1))
                        } }>Cancel</Button>
                        <Button size="sm" variant="primary" onClick={ () => bulkUpdateRenewalDate(multiCheckedIds) }>Confirm</Button>
                    </Modal.Footer>
                </Modal>
            }

            {
                showBulkEditAdminEmailModal &&
                <Modal show onHide={ () => {
                    setShowBulkEditAdminEmailModal(false)
                    setAdminEmail("")
                }} centered>
                    <Modal.Header>
                        <Modal.Title>Update Admin Email</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className='mb-3 row col-12'>
                            <Form.Label className='form-label fs-sm col-form-label col-md-3'>
                                Admin Email
                            </Form.Label>
                            <div className="col-md-9 d-flex align-items-center">
                                <Form.Control size='sm' defaultValue={adminEmail} type="email" onChange={ e => setAdminEmail(e.target.value)} />
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button size="sm" variant='secondary' onClick={ () => {
                            setShowBulkEditAdminEmailModal(false)
                            setAdminEmail("")
                        } }>Cancel</Button>
                        <Button size="sm" variant="primary" onClick={ () => bulkUpdateAdminEmail(multiCheckedIds) }>Confirm</Button>
                    </Modal.Footer>
                </Modal>
            }

            {
                showBulkEditBillToModal &&
                <Modal show onHide={ () => {
                    setShowBulkEditBillToModal(false)
                    setBillTo("")
                }} centered>
                    <Modal.Header>
                        <Modal.Title>Update Bill To Email</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className='mb-3 row col-12'>
                            <Form.Label className='form-label fs-sm col-form-label col-md-3'>
                                Bill To Email
                            </Form.Label>
                            <div className="col-md-9 d-flex align-items-center">
                                <Form.Control size='sm' defaultValue={billTo} type="email" onChange={ e => setBillTo(e.target.value)} />
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button size="sm" variant='secondary' onClick={ () => {
                            setShowBulkEditBillToModal(false)
                            setBillTo("")
                        } }>Cancel</Button>
                        <Button size="sm" variant="primary" onClick={ () => bulkUpdateBillTo(multiCheckedIds) }>Confirm</Button>
                    </Modal.Footer>
                </Modal>
            }

            {
                errorModal &&
                <Modal show onHide={ () => setErrorModal(null) } centered>
                    <Modal.Header>
                        <Modal.Title>Error</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        { errorModal.errorMessage }
                    </Modal.Body>
                    <Modal.Footer>
                        <Button size="sm" variant='secondary' onClick={ () => setErrorModal(null) }>Close</Button>
                    </Modal.Footer>
                </Modal>
            }
        </Container>
    )
}